import { useEffect, useRef, useState } from 'react'
import { styled } from '@linaria/react'
import { MinimalAlert, MinimalCard, MinimalSwitch } from '@ovasabi/ui-minimal'
import { minimalVars as v } from '@ovasabi/ui-minimal/tokens'
import { PageHeader, Screen } from '../../components/chow'
import { Button } from '../../components/ui/Button'
import { commandPayload, commands } from '../../lib/runtime'
import { isNativeShell } from '../../lib/shell'
import { useBackNav, useChooseChow } from '../choosechow/ChooseChowContext'
import { InstallAppAction } from './InstallApp'
import { disablePush, enablePush, pushState, pushSupported, testPush } from './push'
import { MAX_VOLUME, MIN_VOLUME, readSoundPreferences, saveSoundPreferences, SoundPlayer, type SoundPreferences } from './sounds'

const Style = {
  Stack: styled.div`display: grid; gap: ${v.space.sm}; max-width: 760px; width: 100%; margin: 0 auto; padding: ${v.space.xs} 0 ${v.space.sm};`,
  Content: styled.div`display: grid; gap: ${v.space.xs}; padding: ${v.space.xs}; h2 { margin: 0; /* 19.2px sits 14.6% from h2Size, so no token matches. */ font-size: 1.2rem; } p { margin: 6px 0 0; color: ${v.color.textSecondary}; line-height: 1.6; }`,
  Row: styled.label`display: flex; align-items: center; justify-content: space-between; gap: 24px; min-height: 48px; cursor: pointer; input { width: 20px; height: 20px; accent-color: ${v.color.brand}; flex-shrink: 0; } small { display: block; color: ${v.color.textSecondary}; margin-top: 4px; line-height: 1.5; }`,
  Actions: styled.div`display: flex; flex-wrap: wrap; gap: 10px; > * { min-height: 44px; }`,
  Volume: styled.label`display: grid; gap: 12px; input { width: 100%; accent-color: ${v.color.brand}; min-height: 32px; }`,
}

export function NotificationSettings() {
  const { profileId, isChef } = useChooseChow()
  const back = useBackNav()
  const [preferences, setPreferences] = useState(() => readSoundPreferences(profileId))
  const [publicKey, setPublicKey] = useState('')
  const [checking, setChecking] = useState(true)
  const [enabled, setEnabled] = useState(false)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')
  const preview = useRef<SoundPlayer | null>(null)
  const supported = pushSupported()
  useEffect(() => {
    let current = true
    preview.current = new SoundPlayer()
    if (supported) {
      void Promise.all([commands.notifications.settings(), pushState(profileId)]).then(([response, on]) => {
        if (!current) return
        const settings = commandPayload(response)
        if (settings.enabled && typeof settings.public_key === 'string') setPublicKey(settings.public_key)
        setEnabled(on)
      }).catch(() => { if (current) setError('We could not check notifications. Please try again later.') })
        .finally(() => { if (current) setChecking(false) })
    } else setChecking(false)
    return () => { current = false; preview.current?.dispose(); preview.current = null }
  }, [profileId, supported])

  const update = (patch: Partial<SoundPreferences>) => {
    const next = { ...preferences, ...patch }
    setPreferences(next)
    saveSoundPreferences(profileId, next)
  }
  const play = async (cue: 'message' | 'new_order' = 'message') => {
    const audio = preview.current
    if (!audio) return
    await audio.unlock()
    audio.play(cue, `preview:${cue}:${Date.now()}`, preferences, true)
  }
  const act = async (action: 'toggle' | 'test') => {
    setBusy(true); setError(''); setNotice('')
    try {
      if (action === 'test') { await testPush(); setNotice('Test sent. Look for a ChooseChow notification on this device.') }
      else if (enabled) { await disablePush(); setEnabled(false); setNotice('Notifications are off on this device.') }
      else { await enablePush(profileId, publicKey); setEnabled(true); setNotice('You’re all set. Order updates and messages will reach you on this device.') }
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Something went wrong. Please try again.') }
    finally { setBusy(false) }
  }

  return <Screen header={<PageHeader title="Sounds & notifications" onBack={back} />}><Style.Stack>
      <MinimalCard><Style.Content>
        <div><h2>Sounds</h2><p>{isChef ? 'A bell for new paid orders, a chime for everything else.' : 'A quiet chime for messages and order updates.'}</p></div>
        <MinimalSwitch label="Play sounds" hint="On this device" checked={preferences.enabled} onCheckedChange={(enabled) => update({ enabled })} />
        <Style.Volume>Volume · {Math.round((preferences.volume / MAX_VOLUME) * 100)}%<input aria-label="Sound volume" type="range" min={MIN_VOLUME} max={MAX_VOLUME} step="0.01" value={preferences.volume} onChange={(e) => update({ volume: Number(e.target.value) })} /></Style.Volume>
        <Style.Actions><Button variant="secondary" onClick={() => void play()} aria-label="Preview ChooseChow chime">Chime ♪</Button>{isChef && <Button variant="secondary" onClick={() => void play('new_order')} aria-label="Preview new order bell">Order bell ♪</Button>}</Style.Actions>
      </Style.Content></MinimalCard>
      <MinimalCard><Style.Content>
        <div><h2>Stay in the loop</h2><p>Get order updates and new messages even when ChooseChow is closed. Message contents stay private.</p></div>
        {!supported ? <p>{isNativeShell() ? 'Device notifications are not available in this app version yet. Sounds work while the app is open.' : 'Use a browser that supports notifications. On iPhone or iPad, add ChooseChow to your Home Screen and open it there.'}</p>
          : <><p>{checking ? 'Checking this device…' : enabled ? 'Notifications are on for this device.' : publicKey ? 'You decide when to turn them on.' : error ? 'Your in-app updates still work.' : 'Device notifications are coming soon. Your in-app updates still work.'}</p>
            <Style.Actions><Button disabled={checking || busy || (!publicKey && !enabled)} onClick={() => void act('toggle')}>{enabled ? 'Turn off notifications' : 'Enable notifications'}</Button>{enabled && <Button variant="secondary" disabled={busy} onClick={() => void act('test')}>Send a test</Button>}</Style.Actions>
            <p>Your browser and device settings control the notification sound.</p></>}
        {error && <MinimalAlert tone="danger" role="alert">{error}</MinimalAlert>}
        {notice && <MinimalAlert tone="success" role="status">{notice}</MinimalAlert>}
      </Style.Content></MinimalCard>
      {!isNativeShell() && <MinimalCard><Style.Content>
        <div><h2>Install the app</h2><p>Add ChooseChow to your Home Screen for a full-screen app that opens in one tap. On iPhone and iPad, notifications only work from the installed app.</p></div>
        <InstallAppAction />
      </Style.Content></MinimalCard>}
    </Style.Stack></Screen>
}
